// ============================================================
// OMYA Digital Plant – English Translation Dictionary
// Usage: import { t } from '@/i18n';  then t('key')
// ============================================================

const en = {
  // ── Navigation ──
  'nav.main': 'MAIN',
  'nav.additional': 'MORE',
  'nav.dashboard': 'Overview',
  'nav.supplyChain': 'Supply chain',
  'nav.trucks': 'Arrivals',
  'nav.planning': 'Planning',
  'nav.orders': 'Orders',
  'nav.sapIntegration': 'SAP Integration',
  'nav.aiVision': 'AI / VISION',
  'tour.open': 'Guide',
  'tour.next': 'Next',
  'tour.prev': 'Back',
  'tour.done': 'Done',
  'tour.welcome.title': 'Welcome to OMYA Digital Plant',
  'tour.welcome.text': 'In 2 minutes we show how the tool handles truck arrivals at Pomezí: today 10–15 trucks a day do not come and do not say so. You can close the guide and replay it any time.',
  'tour.states.title': "Today's loadings at a glance",
  'tour.states.text': 'You see right away how many loadings have no information. This is the number we want as low as possible every day.',
  'tour.perHour.title': 'Arrivals per hour',
  'tour.perHour.text': 'Shows the 6–9 and 12–14 peaks against gate capacity. We do not dictate windows, we just know what is coming. Red are trucks we know nothing about.',
  'tour.banner.title': 'What needs action',
  'tour.banner.text': 'The overview only shows what is happening. Here you see how many loadings need attention, and one click takes you to the Actions queue.',
  'tour.queue.title': 'Actions queue',
  'tour.queue.text': 'The planner sees only what has to be solved: follow up, call, send the dispatcher a link, reschedule. What is solved leaves the queue. The goal is an empty queue.',
  'tour.urgency.title': 'Carrier follow-up at 13:00',
  'tour.urgency.text': 'At 13:00 the system compares the plan with reality and contacts the right carrier by SMS, WhatsApp or e-mail in their language. The planner approves, or it runs fully automatically.',
  'tour.replies.title': 'Replies come in live',
  'tour.replies.text': 'The demo is running now: AI reads replies in Polish, GPS finds a truck on its way, and whoever does not reply within 30 minutes gets a call. The queue empties on its own; the planner is left with two loadings.',
  'tour.stats.title': 'Results in numbers',
  'tour.stats.text': 'Long-term statistics: no-shows without notice, loadings informed at 13:00, follow-ups and trucks at the Jeseník parking, always against the period before the pilot.',
  'tour.carriers.title': 'Carrier scorecard',
  'tour.carriers.text': 'Who announces, who does not show up and how fast they reply. Input for operational contracts and price talks.',
  'tour.restart.title': 'Replay the guide any time',
  'tour.restart.text': 'Use this button to start the guide again, for example for colleagues. Thank you for your attention.',
  'announce.button': 'Link for dispatcher',
  'announce.previewTitle': 'Carrier announcement',
  'announce.previewHint': 'This is what the carrier\'s dispatcher sees on the phone. What they save shows up in Arrivals right away.',
  'announce.openLink': 'Open the link in a new window',
  'announce.msg.saved': 'Carrier announcement',
  'announce.msg.noTruck': 'Carrier announcement: no truck today',
  'sources.title': 'Plan sources',
  'sources.intro': 'Where the loading plan (who comes when) can come from. The pilot starts with manual import; automatic sources follow as OMYA IT allows.',
  'sources.pilot': 'Recommended for the pilot',
  'sources.status.live': 'Live',
  'sources.status.demo': 'Demo data',
  'sources.status.ready': 'Ready to connect',
  'sources.effort': 'Effort',
  'sources.effortLevel.low': 'low',
  'sources.effortLevel.medium': 'medium',
  'sources.effortLevel.high': 'high',
  'sources.needsIt': 'Needs OMYA IT',
  'sources.noIt': 'No OMYA IT needed',
  'sources.how': 'How it works',
  'sources.fromOmya': 'What we need from OMYA',
  'sources.lastRun': 'Last automatic import today at',
  'sources.loadings': 'loadings from SAP',
  'sources.open': 'Open import',
  'sources.manual.title': 'Manual import (Excel, CSV, clipboard)',
  'sources.manual.how': 'The planner pastes a table from SAP or Excel; the app recognises the columns and checks the rows.',
  'sources.manual.it': 'Nothing, works right away.',
  'sources.sapEmail.title': 'Daily e-mail from SAP',
  'sources.sapEmail.how': 'Every morning SAP sends the loading report (XLSX/CSV) to the app address, which reads it on its own.',
  'sources.sapEmail.it': 'A scheduled job in SAP and an address to receive the report.',
  'sources.folder.title': 'Shared folder / SharePoint / SFTP',
  'sources.folder.how': 'The export lands in a shared folder; the app fetches it every 15 minutes.',
  'sources.folder.it': 'A folder and read access for the app.',
  'sources.plannerExcel.title': 'Planner\'s Excel (Microsoft 365)',
  'sources.plannerExcel.how': 'The planner keeps working in her Excel on SharePoint; the app reads it continuously.',
  'sources.plannerExcel.it': 'Consent for the app in Microsoft 365 and a fixed table structure.',
  'sources.carrierLink.title': 'Announcements from carriers',
  'sources.carrierLink.how': 'The carrier\'s dispatcher adds plate, driver and arrival to their loadings via a link.',
  'sources.carrierLink.it': 'Contacts of the dispatchers of the 4–6 main carriers.',
  'sources.weighbridge.title': 'Weighbridge and gate',
  'sources.weighbridge.how': 'The weighbridge system or the delivery status in SAP tells who entered and who is loaded.',
  'sources.weighbridge.it': 'Access to the weighbridge system or its export.',
  'sources.sapDirect.title': 'Direct SAP connection',
  'sources.sapDirect.how': 'The app reads deliveries and shipments directly from SAP (OData / RFC) in real time.',
  'sources.sapDirect.it': 'A technical user and API in SAP, network access, approval by global IT.',
  'import.title': 'Plan import',
  'import.done': 'Loadings imported',
  'import.xlsxHint': 'We cannot read .xlsx directly yet. Copy the table from Excel (Ctrl+C) and paste it here, or save it as CSV.',
  'import.file': 'Upload CSV',
  'import.useSample': 'Use sample data',
  'import.downloadSample': 'Download sample file',
  'import.paste.title': 'Paste the loading plan',
  'import.paste.hint': 'Copy the table from SAP or Excel (Ctrl+C) and paste it here (Ctrl+V). The first row holds the column names.',
  'import.paste.placeholder': 'Delivery\tCustomer\tMaterial\tQuantity\tCarrier\tDate\tTime\tType',
  'import.photo.title': 'Photo or PDF of a printout',
  'import.photo.hint': 'Take a photo of the printed plan. AI extracts the rows, you only check them.',
  'import.photo.demo': 'Demo: read from a photo',
  'import.photo.simulated': 'Demo: AI read the rows from a photo (simulated)',
  'import.photo.upload': 'Upload a photo or PDF',
  'import.photo.read': 'The AI read {n} rows, check them below.',
  'import.photo.demoNote': 'Without the AI key the sample plan is loaded instead of the file.',
  'import.photo.tooLarge': 'The file is larger than 4.8 MB.',
  'import.mapping.title': 'Which column is what',
  'import.mapping.hint': 'Check the mapping the first time. It is saved and the same file loads by itself next time. * = required.',
  'import.mapping.none': '— not used —',
  'import.field.orderId': 'Delivery',
  'import.field.customer': 'Customer',
  'import.field.material': 'Material',
  'import.field.weight': 'Quantity (t)',
  'import.field.carrier': 'Carrier',
  'import.field.date': 'Date',
  'import.field.time': 'Time',
  'import.field.type': 'Type (silo/pallets)',
  'import.preview.title': 'Check before import',
  'import.preview.ok': 'OK',
  'import.preview.failed': 'With errors',
  'import.preview.import': 'Import loadings',
  'import.preview.problems': 'Problem',
  'import.problem.missing.orderId': 'delivery missing',
  'import.problem.missing.carrier': 'carrier missing',
  'import.problem.missing.date': 'date missing',
  'import.problem.missing.time': 'time missing',
  'import.problem.missing.material': 'material missing',
  'import.problem.unknownCarrier': 'unknown carrier',
  'import.problem.badDate': 'invalid date or time',
  'import.problem.pastDate': 'date in the past',
  'import.problem.duplicate': 'delivery already planned',
  'nav.planSetup': 'Plan setup',
  'nav.stats': 'Statistics',
  'stats.title': 'Statistics',
  'stats.demoData': 'Demo data',
  'stats.demoHint': 'Illustrative preview of the statistics after the carrier follow-up is introduced. Baselines from the client.',
  'stats.kpiPeriod': 'Average of the last 30 days vs. the period before the pilot',
  'stats.tab.overview': 'KPI overview',
  'stats.tab.carriers': 'Carriers',
  'stats.carrier.name': 'Carrier',
  'stats.carrier.trucks': 'trucks',
  'stats.carrier.loadings': 'Loadings',
  'stats.carrier.announcedPct': 'Announced',
  'stats.carrier.noShowPct': 'No-shows without notice',
  'stats.carrier.outOfHours': 'Out of hours',
  'stats.carrier.replyMedian': 'Reply (median)',
  'stats.carrier.onTimePct': 'ETA ±30 min',
  'stats.carrier.legend': 'Arrows: change vs. the previous period of the same length (green = better). Red: 15 % or more no-shows without notice. Click a carrier for its trend.',
  'stats.carrier.weeklyNoShows': 'No-shows without notice per week',
  'stats.carrier.weeklyOnTime': 'ETA accuracy per week',
  'stats.carrier.detailHint': 'Input for operational contracts and price talks with the carrier. Demo data.',
  'rating.title': 'Monthly carrier rating',
  'rating.open': 'Monthly rating for print',
  'rating.back': 'Back to carriers',
  'rating.month': 'Month',
  'rating.partial': 'partial',
  'rating.print': 'Print',
  'rating.workDays': '{days} working days',
  'rating.pilotShare': 'automatic follow-up on {pct} % of days',
  'rating.thisMonth': 'This month',
  'rating.prevMonth': 'Previous month',
  'rating.change': 'Change',
  'rating.findings': 'Findings',
  'rating.finding.noShowAlarm': 'No-shows without notice above the 15 % limit: a topic for the operational contract.',
  'rating.finding.noShowBetter': 'No-shows without notice went down compared with the previous month.',
  'rating.finding.noShowWorse': 'No-shows without notice went up compared with the previous month.',
  'rating.finding.slowReply': 'Replies to follow-ups slowly (median above 30 min).',
  'rating.finding.onTime': 'Reliable ETA: 90 % or more arrivals within ±30 min.',
  'rating.notes': 'Meeting notes',
  'rating.footer': 'OMYA Pomezí · Demo data, same measures as the carrier scorecard on Statistics.',
  'csv.export': 'Export CSV',
  'csv.date': 'Date',
  'csv.pilot': 'Pilot',
  'csv.planned': 'Planned loadings',
  'csv.country': 'Country',
  'csv.yes': 'yes',
  'csv.no': 'no',
  'stats.tab.site': 'Site',
  'stats.tab.reports': 'Reports',
  'reports.readyToConnect': 'Ready to connect',
  'reports.send': 'Send by e-mail',
  'reports.sendToast': 'Sending by e-mail is ready to be connected. Nothing is sent in the demo.',
  'reports.daily.title': 'Daily summary',
  'reports.daily.schedule': 'Sent every working day at 16:00 to planners and dispatch',
  'reports.daily.planned': 'Planned',
  'reports.daily.loaded': 'Loaded',
  'reports.daily.urgencies': 'Follow-ups: {total}, {auto} resolved automatically and {planner} handed to the planner.',
  'reports.daily.missing': 'Did not come',
  'reports.daily.open': 'Still open, with the planner',
  'reports.daily.none': 'None',
  'reports.weekly.title': 'Weekly report for management',
  'reports.weekly.schedule': 'Sent on Monday at 7:00',
  'reports.weekly.thisWeek': 'This week',
  'reports.weekly.worst': 'Most problematic carriers',
  'reports.weekly.noShowShort': 'without notice',
  'stats.site.heatmapTitle': 'Arrivals by day and hour',
  'stats.site.heatmapHint': 'Average arrivals per hour. Gate capacity: {capacity} / h',
  'stats.site.heatmapLegend': "The darker, the closer to the gate's capacity. Red: more trucks than the gate handles in an hour. Capacities are indicative, to be confirmed by the plant.",
  'stats.site.gate.CLS': 'CLS (silo)',
  'stats.site.gate.PST': 'PST (pallets)',
  'stats.site.dwellTitle': 'Time on site',
  'stats.site.dwellHint': 'Median for the selected period; 90 % of trucks are done within the time shown',
  'stats.site.silo': 'Silo',
  'stats.site.pallet': 'Pallets',
  'stats.site.p90': '90 % within',
  'stats.site.dwellMinutes': 'Total on site (gate → departure)',
  'stats.site.waitMinutes': 'Waiting before loading',
  'stats.site.loadMinutes': 'Loading',
  'stats.last30': '30 days',
  'stats.last90': '90 days',
  'stats.beforePilot': 'before pilot',
  'stats.pilotStart': 'Pilot start',
  'stats.unit.perDay': '/ day',
  'stats.unit.trucksPerNight': 'trucks / night',
  'stats.kpi.noShows': 'No-shows without notice',
  'stats.kpi.informed': 'Loadings informed at 13:00',
  'stats.kpi.urgencies': 'Carrier follow-ups',
  'stats.kpi.autoResolved': 'resolved automatically',
  'stats.kpi.parking': 'Jeseník parking',
  'stats.chart.noShows': 'No-shows without notice per day',
  'stats.chart.noShowsHint': 'Trucks that did not come and did not say so. 10–15 a day before the pilot (client).',
  'stats.chart.informed': 'Loadings informed at 13:00',
  'stats.chart.informedHint': "Share of today's loadings where we know at 13:00 whether and when they come.",
  'stats.chart.urgencies': 'Follow-ups: resolved automatically vs. by the planner',
  'stats.chart.urgenciesHint': 'Before the pilot the planner called by hand and nothing was recorded.',
  'stats.chart.parking': 'Trucks at the Jeseník parking per night',
  'stats.chart.parkingHint': 'Fewer trucks out of hours and without notice mean less need for the holding parking.',
  'stats.series.auto': 'Automatic',
  'stats.series.planner': 'Planner',
  'stats.tab.urgency': 'Follow-ups',
  'stats.urgency.period': 'Pilot days in the period: {days}, follow-ups in total: {total}',
  'stats.urgency.replyPct': 'Reply rate',
  'stats.urgency.replyMedian': 'Median reply',
  'stats.urgency.escalated': 'Escalated to a call',
  'stats.urgency.escalatedHint': 'No reply within 30 min',
  'stats.urgency.resolution': 'Automatic / planner',
  'stats.urgency.resolutionHint': 'Resolved on its own vs. handed to the planner',
  'stats.urgency.byChannel': 'Follow-ups by channel',
  'stats.urgency.channel': 'Channel',
  'stats.urgency.sent': 'Sent',
  'stats.urgency.cost': 'Cost',
  'stats.urgency.czk': 'CZK',
  'stats.urgency.channelLegend': "The first message goes out on the carrier's own channel. Without a reply within 30 minutes a voice call follows, then the planner. Call reply time is measured from dialling.",
  'stats.urgency.savingsTitle': 'Estimated savings per month',
  'stats.urgency.hoursPerMonth': 'h / month',
  'stats.urgency.savedHours': 'Planner time saved: follow-ups no longer handled by hand',
  'stats.urgency.czkPerMonth': 'CZK / month',
  'stats.urgency.channelCost': 'Cost of messages and calls',
  'stats.urgency.assumption': 'Assumption: a manual follow-up takes the planner {minutes} min (find the contact, call, note the result), a month has {days} working days. Price per message or call: {prices}. Demo data, indicative prices.',
  'nav.views': 'Page views',
  'ai.live': 'AI',
  'ai.demo': 'AI demo',
  'ai.liveHint': 'Answer from Claude (AI).',
  'ai.demoHint': 'Prepared demo answer: the AI key is not set yet or the AI did not answer.',
  'stats.value.title': 'Value of the pilot for the plant',
  'stats.value.month': 'month',
  'stats.value.roi': 'Pays back {roi}× at {price} a month',
  'stats.value.parking': 'Jeseník parking',
  'stats.value.parkingCalc': 'trucks per night {before} → {now} (−{pct} %) of {cost} CZK a month',
  'stats.value.noShows': 'Fewer no-shows without notice',
  'stats.value.noShowsCalc': '{count} fewer no-shows a month × {cost} CZK',
  'stats.value.planner': 'Planner time',
  'stats.value.plannerCalc': '{hours} h × {cost} CZK',
  'stats.value.messages': 'Cost of messages and calls',
  'stats.value.messagesCalc': 'SMS, WhatsApp, e-mail, voice calls',
  'stats.value.source.client': "client's number",
  'stats.value.source.estimate': 'our estimate',
  'stats.value.noShowCostInput': 'Cost of one no-show',
  'stats.value.priceInput': 'Software price per month',
  'stats.value.note': "Parking cost and no-show cost to be confirmed with OMYA's figures.",
  'ai.energy.title': 'Recommended grinding plan for tomorrow',
  'ai.energy.hint': 'The AI places the mills in the cheapest hours by OTE prices. The app computes costs and savings.',
  'ai.energy.recommend': 'Recommend a plan (AI)',
  'ai.energy.habit': 'Today (start at 6:00)',
  'ai.energy.plan': 'As recommended',
  'ai.energy.saving': 'Saving per day',
  'ai.energy.legend': "Colour = price band (green cheap, red peak), outline = today's habit. Prices: model OTE working day.",
  'ai.doc.title': 'Document from a photo',
  'ai.doc.upload': 'Take or upload a document',
  'ai.doc.expected': 'expected',
  'ai.doc.attach': 'Save to the loading',
  'ai.doc.attached': 'Saved to the communication.',
  'ai.doc.tooLarge': 'The file is larger than 4.8 MB.',
  'ai.doc.allMatch': 'everything matches the loading',
  'ai.doc.type.cmr': 'CMR',
  'ai.doc.type.weighbridge': 'Weighbridge ticket',
  'ai.doc.type.delivery-note': 'Delivery note',
  'ai.doc.type.other': 'Other document',
  'ai.doc.field.plate': 'Plate',
  'ai.doc.field.orderId': 'Delivery',
  'ai.doc.field.weight': 'Weight',
  'ai.voice.call': 'Call with the AI voice:',
  'ai.voice.ringing': 'Dialling…',
  'ai.voice.inProgress': 'Call in progress',
  'ai.voice.ended': 'Call ended',
  'ai.voice.simulation': 'Simulated call',
  'ai.voice.result': 'Outcome',
  'ai.voice.noAnswer': 'The dispatcher does not answer, the loading goes to the planner.',
  'ai.assistant.title': 'AI assistant',
  'ai.assistant.ask': 'Ask the assistant',
  'ai.assistant.thinking': 'Thinking…',
  'ai.assistant.back': 'Back to search',
  'ai.brief.prepare': 'Prepare talking points (AI)',
  'ai.brief.title': 'Talking points for the carrier meeting',
  'ai.brief.goal': 'Goal',
  'ai.brief.arguments': 'Arguments',
  'ai.brief.proposals': 'Proposed terms',
  'ai.brief.agreements': 'What to agree on',
  'ai.brief.note': "AI draft from the month's numbers. Check and adjust before the meeting.",
  'ai.replan.propose': 'Propose a solution (AI)',
  'ai.replan.title': 'Re-planning proposal',
  'ai.replan.risk': 'Risk',
  'ai.replan.accept': 'Accept',
  'ai.replan.reject': 'Reject',
  'ai.replan.accepted': 'Accepted: the loading is re-planned, noted in the communication.',
  'ai.replan.rejected': 'Rejected, the plan stays as it is.',
  'ai.comment.title': 'AI commentary',
  'ai.comment.loading': 'Writing the commentary…',
  'ai.ask.title': 'Ask the statistics',
  'ai.ask.placeholder': 'E.g. Which carrier most often does not come without notice?',
  'ai.ask.send': 'Ask',
  'ai.ask.source': 'Source',
  'ai.ask.q1': 'How many trucks do not come without notice, and how was it before the pilot?',
  'ai.ask.q2': 'Which carrier is the most problematic?',
  'ai.ask.q3': 'How much time did the automatic follow-up save?',
  'ai.ask.q4': 'How is the share informed at 13:00 developing?',
  'ai.reply.title': 'Simulate a carrier reply',
  'ai.reply.placeholder': "Type the carrier's reply, e.g. in Polish…",
  'ai.reply.read': 'Read the reply',
  'ai.reply.unclear': 'Unclear',
  'ai.reply.confidence': 'Confidence',
  'ai.reply.reason': 'Reason',
  'ai.reply.noTranslation': 'Translation needs the AI with a key.',
  'ai.reply.applied': 'Applied automatically, the reply is in the communication.',
  'ai.reply.confirm': 'Confirm',
  'ai.reply.dismiss': 'Discard',
  'ai.reply.readByAi': 'read by AI',
  'nav.focus': 'CURRENT FOCUS',
  'nav.concepts': 'OPTIONS / CONCEPTS',
  'nav.conceptsHint': 'Ideas for later, no real data yet',
  'concept.badge': 'Concept',
  'concept.hint': 'Preview of a possible extension. Data is illustrative and the module is not part of the pilot plan yet.',
  'nav.branchDetection': 'Branch detection',
  'nav.visualFactory': 'Visual Factory',

  // ── Branch detection ──
  'branchDetection.title': 'Branch detection',
  'branchDetection.description': 'AI module for detecting branches on the conveyor belt. Identifies unwanted contamination in stone in real time.',
  'branchDetection.demo': 'Detection demo',
  'branchDetection.detected': 'Detected',
  'branchDetection.branches': 'branches',
  'branchDetection.accuracy': 'Accuracy',
  'branchDetection.zone': 'Detection zone',
  'branchDetection.live': 'Live',
  'branchDetection.label': 'branch',
  'branchDetection.shift.title': 'Morning shift: findings on the belt',
  'branchDetection.shift.total': 'Findings',
  'branchDetection.shift.perHour': 'Findings per hour',
  'branchDetection.shift.note': 'Concept demo on illustrative data. A real version needs a camera over the belt and a trained image model.',
  'branchDetection.outcome.ejected': 'Ejected by the flap',
  'branchDetection.outcome.review': 'To check',
  'branchDetection.outcome.false': 'False alarm',

  // ── Header ──
  'header.search': 'Search',
  'header.notifications': 'Notifications',
  'header.activeAlerts': 'active alerts',
  'header.markAllRead': 'Mark all read',
  'header.allClear': 'All clear',
  'header.live': 'LIVE',
  'header.profile': 'Profile',
  'header.settings': 'Settings',
  'header.signOut': 'Sign out',
  'header.justNow': 'just now',
  'header.minAgo': 'min ago',
  'header.hrAgo': 'h ago',
  'header.dAgo': 'd ago',
  'header.language': 'Language',

  // ── Command Palette ──
  'cmd.placeholder': 'Search orders, trucks, nodes...',
  'cmd.noResults': 'No results.',
  'cmd.pages': 'Pages',
  'cmd.orders': 'Orders',
  'cmd.trucks': 'Trucks',
  'cmd.supplyChainNodes': 'Supply chain nodes',
  'cmd.util': 'util.',

  // ── Dashboard ──
  'dashboard.title': 'Overview',
  'dashboard.plant': 'Pomezí Plant',
  'dashboard.totalOutputToday': 'Total output today',
  'dashboard.morningShift': 'Morning shift',
  'dashboard.afternoonShift': 'Afternoon shift',
  'dashboard.nightShift': 'Night shift',
  'dashboard.all': 'All',
  'dashboard.am': 'AM',
  'dashboard.pm': 'PM',
  'dashboard.night': 'Night',
  'dashboard.operationsActive': 'Operations active',
  'dashboard.linesInOperation': 'lines in operation',
  'dashboard.loadings': 'loadings',
  'dashboard.todaysOutput': "Today's output",
  'dashboard.vsYesterday': 'vs yesterday',
  'dashboard.plantUtilization': 'Plant utilization',
  'dashboard.linesActive': 'active lines',
  'dashboard.oee': 'OEE',
  'dashboard.loadingsToday': 'Loadings today',
  'dashboard.remaining': 'remaining',
  'dashboard.qualityRate': 'Quality',
  'dashboard.energyCost': 'Energy cost',
  'dashboard.costPerTonne': 'Cost per tonne',
  'dashboard.costPerTonneSub': 'Live OTE × milling energy',
  'dashboard.offPeakUntil': 'OFF-PEAK until 14:00',
  'dashboard.productionLast7Days': 'Production – last 7 days',
  'dashboard.actual': 'Actual',
  'dashboard.target': 'Target',
  'dashboard.lineUtilization': 'Line utilization',
  'dashboard.utilization': 'Utilization',
  'dashboard.activeAlerts': 'Active alerts',
  'dashboard.oeeFull': 'OEE (Overall Equipment Effectiveness)',

  // ── KPI Card ──
  'kpi.target': 'Target',
  'kpi.offPeakUntil14': 'OFF-PEAK until 14:00',

  // ── KPI Drilldown ──
  'drilldown.title': 'Detail',
  'drilldown.todayHourly': 'Today (hourly)',
  'drilldown.thisWeek': 'This week',
  'drilldown.thisMonth': 'This month',
  'drilldown.peakHour': 'Peak hour',
  'drilldown.avgPerHour': 'Avg/hour',
  'drilldown.total': 'Total',
  'drilldown.bestDay': 'Best day',
  'drilldown.weeklyAvg': 'Weekly avg',
  'drilldown.vsTarget': 'vs Target',
  'drilldown.monthTotal': 'Monthly total',
  'drilldown.week1': 'Week 1',
  'drilldown.week2': 'Week 2',
  'drilldown.week3': 'Week 3',
  'drilldown.week4': 'Week 4',

  // ── Alert Strip ──
  'alerts.allOperational': 'All systems operational — no active alerts',
  'alerts.showLess': 'Show less',
  'alerts.showAll': 'Show all',

  // ── Trucks Page ──
  'trucks.title': 'Arrivals',
  'trucks.enRoute': 'En route',
  'trucks.waiting': 'Waiting',
  'trucks.weighIn': 'Weigh-in',
  'trucks.loading': 'Loading',
  'trucks.weighOut': 'Weigh-out',
  'trucks.departed': 'Departed',
  'trucks.total': 'Total',
  'trucks.loadingTimeline': 'Loading timeline',
  'trucks.siteMap': "Site now",
  'trucks.siteLayout': 'Site layout',
  'trucks.plant': 'PLANT',
  'trucks.omyaJesenik': 'OMYA Pomezí',
  'trucks.truckCount': 'trucks',
  'trucks.weighBridge': 'WEIGH BRIDGE',
  'trucks.searchPlaceholder': 'Search trucks...',

  // ── Truck Detail Sheet ──
  'truckDetail.overview': 'Overview',
  'truckDetail.timeline': 'Timeline',
  'truckDetail.order': 'Order',
  'truckDetail.load': 'Load',
  'truckDetail.driver': 'Driver',
  'truckDetail.currentLoad': 'Current load',
  'truckDetail.loaded': 'loaded',
  'truckDetail.remaining': 'remaining',
  'truckDetail.progress': 'Progress',
  'truckDetail.gate': 'Gate',
  'truckDetail.eta': 'ETA',
  'truckDetail.event': 'Event',
  'truckDetail.linkedOrder': 'Linked order',
  'truckDetail.noLinkedOrder': 'No linked order',
  'truckDetail.documents': 'Documents',
  'truckDetail.material': 'Material',
  'truckDetail.targetWeight': 'Target weight',
  'truckDetail.currentWeight': 'Current weight',
  'truckDetail.timestamps': 'Timestamps',
  'truckDetail.arrived': 'Arrived',
  'truckDetail.loadingStarted': 'Loading started',
  'truckDetail.loadingTimeBySack': 'Loading time / packaging',

  // ── Planning Page ──
  'planning.title': 'Planning',
  'planning.week': 'Week',
  'planning.thisWeek': 'This week',
  'planning.weekView': 'Week view',
  'planning.ganttView': 'Gantt view',
  'planning.capacity': 'Capacity',
  'planning.scheduled': 'Scheduled',
  'planning.available': 'Available',
  'planning.materials': 'Materials',
  'planning.weeklySummary': 'Weekly summary',
  'planning.plannedOutput': 'Planned output',
  'planning.actualOutput': 'Actual output',
  'planning.completion': 'Completion',
  'planning.avgUtilization': 'Avg utilization',
  'planning.energyTariffs': 'Energy tariffs',
  'planning.off': 'OFF',
  'planning.peak': 'PEAK',
  'planning.tariffStandard': 'STD',
  'planning.tariffNegative': 'NEG',
  'planning.schedule': '+ Schedule',
  'planning.now': 'Now',
  'planning.material.gcc': 'Limestone GCC',
  'planning.material.pcc': 'Limestone PCC',
  'planning.material.byproduct': 'By-product',
  'planning.productionFlow': 'Production flow',
  'planning.productionFlowDesc': 'Stone (raw) → limestone products (GCC/PCC) → by-product (CaCO3). Processing stone yields main outputs as well as by-product.',
  'planning.mainProduction': 'Main production',
  'planning.byProduct': 'By-product',
  'planning.fraction': 'Fraction',
  'planning.fraction.sand': 'Sand',
  'planning.fraction.fine': 'Fine',
  'planning.fraction.standard': 'Standard',
  'planning.fraction.powder': 'Powder',
  'planning.fractionCycleNote': 'cycle time per tonne',
  'planning.packagingLine': 'Packaging line',
  'planning.packaging.bigBag': 'Big bag (1 t)',
  'planning.packaging.bag25': 'Bags 25 kg',
  'planning.packaging.bag50': 'Bags 50 kg',
  'planning.packaging.bulk': 'Bulk (silo)',
  'planning.linkedTo': 'Linked to',
  'planning.linkedDeps': 'Linked production',
  'planning.linkedDepsDesc': 'Producing this material requires parallel run of the linked items below.',

  'addPlanning.title': 'Add planning entry',
  'addPlanning.material': 'Material',
  'addPlanning.order': 'Order',
  'addPlanning.selectOrder': 'Select order',
  'addPlanning.noOrder': 'No order',
  'addPlanning.energyTariff': 'Energy tariff',
  'addPlanning.tariff': 'tariff',
  'addPlanning.cancel': 'Cancel',
  'addPlanning.add': 'Add',

  'gantt.util': 'util.',
  'gantt.planned': 'planned',
  'gantt.now': 'Now',

  // ── Days of week ──
  'day.mon': 'Mon',
  'day.tue': 'Tue',
  'day.wed': 'Wed',
  'day.thu': 'Thu',
  'day.fri': 'Fri',
  'day.sat': 'Sat',
  'day.sun': 'Sun',

  // ── Orders Page ──
  'orders.title': 'Orders',
  'orders.totalOrders': 'Total orders',
  'orders.totalValue': 'Total value',
  'orders.avgOrder': 'Average order',
  'orders.fulfillmentRate': 'Fulfillment rate',
  'orders.allStatuses': 'All statuses',
  'orders.pending': 'Pending',
  'orders.confirmed': 'Confirmed',
  'orders.inProduction': 'In production',
  'orders.produced': 'Produced',
  'orders.dispatched': 'Dispatched',
  'orders.delivered': 'Delivered',
  'orders.searchPlaceholder': 'Search by order, customer or material...',
  'orders.status': 'Status',
  'orders.number': '#',
  'orders.customer': 'Customer',
  'orders.items': 'Items',
  'orders.progress': 'Progress',
  'orders.value': 'Value',
  'orders.delivery': 'Delivery',
  'orders.showing': 'Showing',
  'orders.of': 'of',
  'orders.ordersCount': 'orders',
  'orders.statusFilter': 'Status',

  // ── Supply Chain ──
  'supplyChain.title': 'Supply chain',
  'supplyChain.addWidget': 'Add widget',
  'supplyChain.widgetAdded': 'Widget added',
  'supplyChain.widgetRemoved': 'Widget removed',
  'supplyChain.node.quarry': 'Quarry',
  'supplyChain.node.crusher': 'Crusher',
  'supplyChain.node.sorting': 'Sorting',
  'supplyChain.node.packaging': 'Packaging',
  'supplyChain.node.bulk': 'Bulk',
  'supplyChain.node.dispatch': 'Dispatch',
  'supplyChain.node.silo': 'Silo',
  'supplyChain.bottleneck': 'Bottleneck',
  'supplyChain.legend': 'Legend',
  'supplyChain.legend.nodeStatus': 'Node status',
  'supplyChain.legend.operational': 'Operational',
  'supplyChain.legend.warning': 'Warning',
  'supplyChain.legend.errorBottleneck': 'Error / Bottleneck',
  'supplyChain.legend.flowStatus': 'Flow status',
  'supplyChain.legend.activeFlow': 'Active flow',
  'supplyChain.legend.reducedFlow': 'Reduced flow',
  'supplyChain.legend.stopped': 'Stopped',
  'supplyChain.legend.capacity': 'Capacity',
  'supplyChain.legend.capacityUnder60': '< 60 %',
  'supplyChain.legend.capacity60to85': '60–85 %',
  'supplyChain.legend.capacityOver85': '> 85 %',
  'supplyChain.status.operational': 'Operational',
  'supplyChain.status.warning': 'Warning',
  'supplyChain.status.error': 'Error',
  'supplyChain.crusherTariffBadge': 'Crusher tariff now',
  'supplyChain.crusherCostNow': 'Cost / t now',
  'supplyChain.siloFillTime': 'fill time',
  'supplyChain.siloCapacity': 'capacity',
  'supplyChain.siloContent': 'content',

  // ── SAP Integration ──
  'sap.title': 'SAP Integration',
  'sap.heading': 'SAP S/4HANA Integration',
  'sap.subtitle': 'Bidirectional data sync',
  'sap.omyaDigitalPlant': 'OMYA Digital Plant',
  'sap.supplyChainTrucks': 'Supply chain · Trucks',
  'sap.planningOrders': 'Planning · Orders',
  'sap.restApi': 'REST API',
  'sap.middleware': 'Middleware',
  'sap.dataMapping': 'Data mapping',
  'sap.validationAuth': 'Validation · Auth',
  'sap.rfcOdata': 'RFC / OData',
  'sap.dataFlows': 'Data flows',
  'sap.syncStatus': 'Sync status',
  'sap.apiLog': 'API Log',
  'sap.dataType': 'Data type',
  'sap.direction': 'Direction',
  'sap.frequency': 'Frequency',
  'sap.lastSync': 'Last sync',
  'sap.status': 'Status',
  'sap.productionOrders': 'Production orders',
  'sap.inventoryLevels': 'Inventory levels',
  'sap.customerMaster': 'Customer master',
  'sap.dispatchConfirmations': 'Dispatch confirmations',
  'sap.loadingPlans': 'Loading plans',
  'sap.qualityResults': 'Quality results',
  'sap.materialSpecs': 'Material specs',
  'sap.financialData': 'Financial data',
  'sap.realTime': 'Real-time',
  'sap.every5Min': 'Every 5 min',
  'sap.daily': 'Daily',
  'sap.onEvent': 'On event',
  'sap.every15Min': 'Every 15 min',
  'sap.weekly': 'Weekly',
  'sap.ppProduction': 'PP (Production)',
  'sap.mmMaterials': 'MM (Materials)',
  'sap.sdSales': 'SD (Sales)',
  'sap.qmQuality': 'QM (Quality)',
  'sap.pmMaintenance': 'PM (Maintenance)',
  'sap.fiFinance': 'FI (Finance)',
  'sap.recordsSynced': 'records synced',
  'sap.synchronized': 'Synchronized',
  'sap.delayed': 'Delayed',
  'sap.time': 'Time',
  'sap.method': 'Method',
  'sap.endpoint': 'Endpoint',
  'sap.duration': 'Duration',
  'sap.infoTitle': 'Visual mockup.',
  'sap.infoText': 'No data transfer happens. Full SAP S/4HANA integration planned for Q3 2026 including secure API, SSO auth, audit logging and real-time sync.',

  // ── Login ──
  'login.welcomeBack': 'Welcome back',
  'login.signInSubtitle': 'Sign in to access the overview',
  'login.email': 'E-mail',
  'login.emailPlaceholder': 'first.last@omya.com',
  'login.password': 'Password',
  'login.forgot': 'Forgot?',
  'login.passwordPlaceholder': 'Enter password',
  'login.signIn': 'Sign in',
  'login.signingIn': 'Signing in...',
  'login.demoMode': 'Demo mode — any credentials accepted',

  // ── Production Order Dialog ──
  'prodDialog.utilization': 'Utilization',
  'prodDialog.planned': 'Planned',
  'prodDialog.actual': 'Actual',
  'prodDialog.productionProgress': 'Production progress',
  'prodDialog.offPeak': 'OFF-PEAK',
  'prodDialog.peak': 'PEAK',
  'prodDialog.shift': 'shift',
  'prodDialog.hourlyOutput': 'Hourly output',
  'prodDialog.order': 'Order',

  // ── Shift labels ──
  'shift.morning': 'Morning',
  'shift.afternoon': 'Afternoon',
  'shift.night': 'Night',

  // ── Add Widget Dialog ──
  'widget.addTitle': 'Add metric widget',
  'widget.addDescription': 'Pick a node and metric to display on the canvas.',
  'widget.sourceNode': 'Source node',
  'widget.selectNode': 'Select node...',
  'widget.metric': 'Metric',
  'widget.selectMetric': 'Select metric...',
  'widget.cancel': 'Cancel',
  'widget.add': 'Add widget',
  'metric.oeeOverall': 'OEE overall',
  'metric.availability': 'Availability',
  'metric.performance': 'Performance',
  'metric.quality': 'Quality',
  'metric.utilization': 'Utilization',
  'metric.currentOutput': 'Current output',
  'metric.maxCapacity': 'Max capacity',
  'metric.activeAlerts': 'Active alerts',
  'metric.hoursRun': 'Hours run',

  // ── OEE Overlay ──
  'oee.plantOee': 'Plant OEE',
  'oee.availability': 'Availability',
  'oee.performance': 'Performance',
  'oee.quality': 'Quality',
  'oee.throughput': 'Throughput',

  // ── Node Detail Panel ──
  'node.currentOutput': 'Current output',
  'node.oeeBreakdown': 'OEE breakdown',
  'node.availability': 'Availability',
  'node.performance': 'Performance',
  'node.quality': 'Quality',
  'node.overall': 'Overall',
  'node.material': 'Material',
  'node.order': 'Order',
  'node.progress': 'Progress',
  'node.throughput': 'Throughput',
  'node.uptime': 'Uptime',
  'node.qualityRate': 'Quality rate',
  'node.utilization': 'Utilization',
  'node.performanceRadar': 'Performance radar',
  'node.speed': 'Speed',
  'node.energy': 'Energy',
  'node.output': 'Output',
  'node.actual': 'Actual',
  'node.target': 'Target',

  // ── Visual Factory ──
  'visualFactory.title': 'Visual Factory',
  'visualFactory.panel.capacity': 'Capacity & utilization',
  'visualFactory.panel.orders': 'Today\'s orders',
  'visualFactory.panel.trucks': 'Trucks now',
  'visualFactory.panel.ote': 'Energy tariff',
  'visualFactory.now': 'NOW',
  'visualFactory.utilized': 'Utilized',
  'visualFactory.free': 'Free',
  'visualFactory.alerts': 'Alerts',
  'visualFactory.shift': 'Shift',
  'visualFactory.costPerTonne': 'Cost / tonne',
  'visualFactory.exitFullscreen': 'Exit fullscreen',
  'visualFactory.todayProduced': 'Produced today',
  'visualFactory.expected': 'Should be at',
  'visualFactory.shiftRemaining': 'Shift remaining',
  'visualFactory.productionLines': 'Production lines',
  'visualFactory.silosAndTrucks': 'Silos & trucks',
  'visualFactory.target': 'target',
  'visualFactory.fullIn': 'Full in',
  'visualFactory.truckEta': 'Truck',
  'visualFactory.collision': 'Collision',
  'visualFactory.collisionLegend': 'Red = silo fills before truck arrives',
  'visualFactory.noTruck': 'No truck scheduled',
  'visualFactory.expeditionToday': 'Dispatched today',
  'visualFactory.trucksOnSite': 'Trucks on site',
  'visualFactory.waitingOver30': 'waiting > 30 min',
  'visualFactory.maintenance': 'Maintenance',
  'visualFactory.daysSinceIncident': 'Days since incident',
  'visualFactory.tickerPeak': 'Reduce fine-fraction milling, shift to off-peak window',
  'visualFactory.tickerNegative': 'Negative price — good time for energy-intensive ops',
  'visualFactory.tickerOffPeak': 'Cheap tariff — good time for powder/fine milling',
  'visualFactory.tickerStandard': 'Standard tariff',
  'visualFactory.backToApp': 'Back to app',
  'visualFactory.whatIsThis': 'What am I looking at?',
  'visualFactory.bannerHint': 'Black line = where we should be right now. White bar = produced.',
  'visualFactory.linesHint': 'Status of the 3 production lines. Green = on target, yellow = slow, red = stopped.',
  'visualFactory.silosHint': 'Silo fill level + truck ETA. Red border = silo fills before the truck arrives (production stops).',
  'visualFactory.tariffTicker': 'Energy tariff (bottom strip)',
  'visualFactory.footerInfo': 'Footer bar — dispatch, maintenance, safety',
  'visualFactory.aboutIntro': 'This screen is for the floor TV. No login, anyone walking past can read it. Refreshes every 30 seconds.',
  'visualFactory.aboutBanner': 'Hero banner at the top. Tonnage produced today vs daily target plus shift remaining. Black vertical line marks where we should be right now if everything ran to plan.',
  'visualFactory.aboutLines': 'Three tiles, one per production line. ANDON dot (green/yellow/red per ISA-101). If a line slows or stops, the tile shows the reason and a stopwatch.',
  'visualFactory.aboutSilos': 'The main event. Per silo: current fill, computed time-to-full at current production rate, and ETA of the next truck. Red border = collision: silo overflows before the truck arrives → call the dispatcher now.',
  'visualFactory.aboutFooter': 'Dispatch today (CZ/PL split), trucks on site (and how many are waiting > 30 min), active maintenance with ETA, days since incident.',
  'visualFactory.aboutTicker': 'Current OTE energy tariff + planning hint (peak = reduce fine-fraction milling, off-peak/negative = good window for energy-intensive work).',
  'visualFactory.aboutFooterNote': 'No sensitive data (driver names, margins, named PL customers). Screen is public within the plant.',

  // ── Status (utils.js) ──
  'status.ok': 'OK',
  'status.warning': 'Warning',
  'status.problem': 'Critical',

  // ── Dashboard additions ──
  'dash.arrivals.title': 'Arrivals today',
  'dash.arrivals.urgency': 'Carrier follow-up at 13:00',
  'dash.arrivals.open': 'Open arrivals',
  'dash.arrivals.informed': 'Informed',
  'dash.parking.title': 'Jeseník parking',
  'dash.parking.month': 'month',
  'dash.parking.source': "Client's cost (estimate)",
  'dash.parking.goal': 'Goal: scale the parking down thanks to announced arrivals.',
  'dash.mobileWarning': 'Heads up — this app is optimized for desktop. Mobile may not render correctly.',
  'dash.productionScorecard': 'Production Scorecard',
  'dash.productionScorecardDesc': 'Module evaluates production status — implementation by external vendor',

  // ── Trucks ──
  'trucks.weighBridgeLabel': 'Weigh bridge',
  'trucks.wbActive': 'Active',
  'trucks.wbIdle': 'Idle',
  'trucks.wbLast': 'Last',
  'trucks.loadingSlots': 'Loading slots',
  'trucks.addSlot': 'Add slot',
  'trucks.slotFree': 'Available',
  'trucks.qr.dontComeNow': 'Hold off — do not approach',
  'trucks.qr.youCanComeSlotFree': 'Cleared to proceed — slot is open',
  'trucks.qr.delay30': 'Delay arrival by 30 min',
  'trucks.qr.goToWeighbridge': 'Proceed to weigh bridge',
  'trucks.qr.waitAtGate': 'Wait at the gate',
  'trucks.dm.hourAway': 'One hour out',
  'trucks.dm.in45': 'Arriving in 45 min',
  'trucks.dm.atGate': 'At the gate',
  'trucks.dm.loadingDone': 'Loading finished?',
  'trucks.dm.thanksDriving': 'Thanks, on my way',
  'trucks.toast.messageSent': 'Message sent (demo)',
  'trucks.noMessagesYet': 'No messages yet',
  'trucks.typeMessage': 'Type a message...',
  'trucks.simulateDriverMessage': 'Simulate driver message',
  'truckDetail.communication': 'Communication',
  'truckDetail.calling': 'Calling',

  // ── Orders / Layout / Header ──
  'layout.digitalPlant': 'DIGITAL PLANT',
  'header.markAllReadToast': 'All notifications marked as read',
  'orders.actionSimulated': 'Action simulated — demo mode',
  'orders.confirm': 'Confirm',
  'orders.dispatch': 'Dispatch',
  'orders.invoice': 'Invoice',
  'orders.tab.overview': 'Overview',
  'orders.tab.items': 'Items',
  'orders.tab.timeline': 'Timeline',
  'orders.tab.delivery': 'Delivery',
  'orders.ico': 'Reg. No.',
  'orders.createdAt': 'Created',
  'orders.confirmedAt': 'Confirmed',
  'orders.material': 'Material',
  'orders.quantity': 'Quantity',
  'orders.pricePerTon': 'Price/t',
  'orders.total': 'Total',
  'orders.productionProgress': 'Production progress',
  'orders.linkedTrucks': 'Linked trucks',
  'orders.noTrucks': 'No trucks',
  'orders.certificates': 'Certificates',
  'orders.noCertificates': 'No certificates yet',

  // ── Planning ──
  'plan.howItWorks': 'How does planning work?',
  'plan.howItWorksDesc': 'The planning module optimizes production based on input data and generates a schedule for every production line.',
  'plan.input': 'Input',
  'plan.inputOrders': 'Orders and required materials',
  'plan.inputCapacities': 'Production line capacities',
  'plan.inputAvailability': 'Material and raw material availability',
  'plan.inputTariffs': 'Energy tariffs (peak / off-peak)',
  'plan.output': 'Output',
  'plan.outputWeeklyPlan': 'Weekly production plan (table / Gantt)',
  'plan.outputAssignment': 'Material assignment to lines and shifts',
  'plan.outputTariffOpt': 'Optimization by energy tariffs',
  'plan.outputTonnes': 'Planned and actual output in tonnes',
  'plan.oteOverlay': 'OTE overlay',
  'plan.oteOverlayToggle': 'Toggle OTE energy overlay',
  'plan.oteSpotNote': 'OTE spot, CZK/MWh, excl. VAT and distribution',
  'plan.oteSpotDaily': 'OTE spot — daily curve',
  'plan.oteRecord': 'Record 5/2024',
  'plan.oteTypical': 'Typical day',
  'plan.min': 'Min',
  'plan.avg': 'Avg',
  'plan.max': 'Max',
  'plan.inNegative': 'in negative',
  'plan.negativeRecordSource': '• min −138.75 EUR/MWh • source OTE a.s.',
  'plan.czkPerMWh': 'CZK/MWh',
  'plan.annualSavingsPotential': 'Annual savings potential',
  'plan.savingsAssumption': 'Shift 50% of peak grinding into OTE off-peak/negative bands.',
  'plan.czkPerYear': 'CZK/year',
  'plan.czkPerWeek': 'CZK/week',
  'plan.pctOfPowerBill': '% of power bill',
  'plan.specificEnergy': 'Specific grinding energy',
  'plan.peakTonnageWeek': 'Peak tonnage (week)',
  'plan.baselineNoOpt': 'Baseline (no optimization)',
  'plan.czk': 'CZK',
  'plan.assumptionsTuning': "Assumptions to be calibrated with Petr's actual consumption.",

  // ── Node detail panel ──
  'node.tab.overview': 'Overview',
  'node.tab.production': 'Production',
  'node.tab.maintenance': 'Maintenance',
  'node.tab.connections': 'Connections',
  'node.currentBatch': 'Current batch',
  'node.output24h': 'Output 24h',
  'node.weeklyOutputVsTarget': 'Weekly output vs target',
  'node.equipment': 'Equipment',
  'node.manufacturer': 'Manufacturer',
  'node.model': 'Model',
  'node.installed': 'Installed',
  'node.operatingHours': 'Operating hours',
  'node.nextMaintenance': 'Next maintenance',
  'node.days': 'days',
  'node.maintenanceHistory': 'Maintenance history',
  'node.date': 'Date',
  'node.type': 'Type',
  'node.description': 'Description',
  'node.duration': 'Duration',
  'node.unplanned': 'Unplanned',
  'node.scheduled': 'Scheduled',
  'node.alerts': 'Alerts',
  'node.linkedOrders': 'Linked orders',
  'node.noLinkedOrders': 'No linked orders',
  'node.linkedTrucks': 'Linked trucks',
  'node.noLinkedTrucks': 'No trucks at this node',

  // ── Mock data (dynamic content used in alerts, timelines, line labels) ──
  'mock.line1': 'Line 1',
  'mock.line2': 'Line 2',
  'mock.line3': 'Line 3',
  'mock.alert.line2Overload': 'Line 2 — overload (92%)',
  'mock.alert.noInfoLoadings': 'Loadings without information – carrier follow-up at 13:00',
  'mock.alert.pstQueue': 'PST gate — 3 trucks waiting',
  'mock.alert.dispatchOk': 'Dispatch — all clear',
  'mock.alert.crusherBearingTemp': 'Crusher bearing temperature rising',
  'mock.alert.tariffPeakSwitch': 'Tariff switching to PEAK at 14:00',
  'mock.alert.orderPending': 'Order ORD-2024-1845 awaiting confirmation',
  'mock.alert.truckEta45': 'Truck 5L9 2341 arriving in 45 min',
  'mock.line.stopReason.slowMillVibration': 'Slow mill — vibration above threshold',
  'mock.event.dispatch': 'Leaving depot',
  'mock.event.arriveGate': 'Arrived at gate',
  'mock.event.weighEmpty123': 'Weigh-in: 12.3 t (empty)',
  'mock.event.weighEmpty128': 'Weigh-in: 12.8 t (empty)',
  'mock.event.weighEmpty131': 'Weigh-in: 13.1 t (empty)',
  'mock.event.weigh125': 'Weigh-in: 12.5 t',
  'mock.event.weigh130': 'Weigh-in: 13.0 t',
  'mock.event.weighInProgress': 'Weighing in progress',
  'mock.event.loadingStartedPST': 'Loading started at PST',
  'mock.event.loadingStartedCLS': 'Loading started at CLS',
  'mock.event.loadingStarted': 'Loading started',
  'mock.event.weighOut365': 'Weigh-out: 36.5 t',
  'mock.event.weighOut368': 'Weigh-out: 36.8 t (loaded)',
  'mock.event.dispatched': 'Dispatched',
  'mock.event.arrived': 'Arrived',
  'mock.event.departed': 'Departed',
  'mock.event.departedFacility': 'Departed from facility',
  'mock.event.waitingGate': 'Waiting for gate assignment',
  'mock.event.waitingSlot': 'Waiting for slot',
  'mock.note.received': 'Received',
  'mock.note.receivedEmail': 'Order received by email',
  'mock.note.receivedPhone': 'Received by phone',
  'mock.note.receivedPortal': 'Received via portal',
  'mock.note.receivedOrderEmail': 'Order received by email',
  'mock.note.receivedOrderPortal': 'Order received via portal',
  'mock.note.receivedOrder': 'Order received',
  'mock.note.confirmed': 'Confirmed',
  'mock.note.confirmedSales': 'Confirmed by sales',
  'mock.note.confirmedSalesTeam': 'Confirmed by sales team',
  'mock.note.started': 'Started',
  'mock.note.startedLine1': 'Started on Line 1',
  'mock.note.startedLine2': 'Started on Line 2',
  'mock.note.startedLine3': 'Started on Line 3',
  'mock.note.productionStarted': 'Production started',
  'mock.note.productionStartedLine1': 'Production started on Line 1',
  'mock.note.productionStartedLine2': 'Production started on Line 2',
  'mock.note.productionDone': 'Production completed',
  'mock.note.productionDoneWaiting': 'Production completed, awaiting dispatch',
  'mock.note.done': 'Completed',
  'mock.note.nightShift': 'Night shift',
  'mock.note.dispatched': 'Dispatched',
  'mock.note.dispatchedTwoTrucks': 'Dispatched — 2 trucks',
  'mock.note.dispatchedDopravaPlus': 'Dispatched via Doprava Plus',
  'mock.note.loadedDispatched': 'Loaded and dispatched',
  'mock.note.loadedOnTruck1J4': 'Loaded onto truck 1J4 9921',
  'mock.note.delivered': 'Delivered',
  'mock.note.deliveredSigned': 'Delivered and signed',
  'mock.nodeAlert.vibrationOverThreshold': 'Vibration sensor above threshold',
  'mock.nodeAlert.bearingTemp78': 'Bearing temperature rising — 78 °C',
  'mock.nodeAlert.crusherChamberWear72': 'Crusher chamber wear 72%',
  'mock.dm.hourAway': "I'm an hour out",
  'mock.dm.gateReady': 'Understood, PST gate will be ready',
  'mock.dm.arriveIn45': 'Arriving in 45 min',
  'mock.dm.holdSlot': "Don't come yet, waiting for slot to free up",
  'mock.dm.waitingParking': 'OK, waiting at the lot',
  'mock.dm.slotFree': 'You can come, the slot is free',
  'mock.dm.atGate': "I'm at the gate",
  'mock.dm.goToScale': 'Proceed to the scale',
  'mock.dm.loadingDone': 'Loading complete?',
  'mock.dm.goToExitScale': 'Yes, proceed to exit scale',
  'mock.dm.delay30': 'Delay arrival by 30 min',
  'mock.dm.arriveLater': 'Understood, will arrive later',
  'mock.dm.eta45': 'ETA 45 minutes',
  'mock.dm.preparingCLS': 'Thanks, preparing CLS slot',
  'mock.ote.historicalLabel': '12 May 2024 (Sunday)',
  'mock.ote.typicalLabel': 'Typical working day',
  'mock.equipment.crusher': 'Crusher',
  'mock.day.Po': 'Mon',
  'mock.day.Út': 'Tue',
  'mock.day.St': 'Wed',
  'mock.day.Čt': 'Thu',
  'mock.day.Pá': 'Fri',
  'mock.day.So': 'Sat',
  'mock.day.Ne': 'Sun',
  // ── Arrivals & loadings / urgency ──
  'arrivals.state.no-info': 'No information',
  'arrivals.state.not-coming': 'Not coming',
  'arrivals.state.no-power': 'Out of hours',
  'arrivals.state.rescheduled': 'Rescheduled',
  'arrivals.state.confirmed': 'Confirmed',
  'arrivals.state.en-route': 'En route (GPS)',
  'arrivals.state.on-site': 'On site',
  'arrivals.state.loaded': 'Loaded',
  'arrivals.channel.sms': 'SMS',
  'arrivals.channel.whatsapp': 'WhatsApp',
  'arrivals.channel.email': 'E-mail',
  'arrivals.channel.phone': 'Phone',
  'arrivals.channel.voice': 'Voice call',
  'arrivals.channel.link': 'Driver link',
  'arrivals.channel.gps': 'GPS',
  'arrivals.channel.system': 'System',
  'arrivals.channel.sap': 'SAP',
  'arrivals.perHour': 'Expected arrivals per hour',
  'arrivals.perHourHint': 'By ETA, otherwise by plan. No loading windows, just the peaks.',
  'arrivals.known': 'Informed',
  'arrivals.unknown': 'No information',
  'arrivals.gateCapacity': 'Gate capacity',
  'arrivals.filter.all': 'All',
  'arrivals.filter.attention': 'Needs attention',
  'arrivals.filter.open': 'Not loaded',
  'arrivals.filter.loaded': 'Loaded',
  'arrivals.filter.risk': 'High risk',
  'ai.risk.label': 'No-show risk',
  'ai.risk.high': 'High risk',
  'ai.risk.medium': 'Medium risk',
  'ai.risk.low': 'Low risk',
  'ai.risk.reason.carrier': 'The carrier did not come without notice for {pct} % of loadings in 30 days',
  'ai.risk.reason.noInfo': 'No information from the carrier yet',
  'ai.risk.reason.noPower': 'The driver is out of driving hours',
  'ai.risk.reason.rescheduled': 'Already rescheduled once',
  'ai.risk.reason.confirmed': 'The carrier confirmed the arrival',
  'ai.risk.reason.enRoute': 'The truck is on the way (GPS)',
  'ai.risk.reason.afternoon': 'Afternoon loading, more no-shows',
  'ai.risk.reason.outOfHours': 'Carrier out of driving hours {n} times in 30 days',
  'ai.risk.reason.noPlate': 'From the import, no plate yet',
  'ai.risk.note': "Estimate from the carrier's history and today's state, not a trained model.",
  'arrivals.type.all': 'All',
  'arrivals.type.silo': 'Silos',
  'arrivals.type.pallet': 'Pallets',
  'arrivals.searchPlaceholder': 'Search plate, carrier, driver, delivery...',
  'arrivals.emptyFilter': 'Nothing to show. All loadings in this filter are resolved.',
  'arrivals.plan': 'Plan',
  'arrivals.eta': 'Arrival',
  'arrivals.tomorrow': 'tomorrow',
  'arrivals.loadedAt': 'Loaded',
  'arrivals.toPlanner': 'Handed to planner',
  'arrivals.powerFrom': 'hours from',
  'arrivals.plannedToday': 'Planned today',
  'actions.bannerText': 'loadings need action – follow-up, a call or rescheduling.',
  'actions.open': 'Open actions',
  'actions.title': 'Actions',
  'actions.progress': 'Resolved',
  'actions.hint': 'Loadings that need something done. What you resolve leaves the queue.',
  'actions.empty': 'All resolved',
  'actions.emptyHint': 'No loading needs an action right now.',
  'actions.urge': 'Follow up',
  'actions.urged': 'Followed up',
  'actions.urgedToast': 'Follow-up sent to the carrier',
  'actions.call': 'Call',
  'actions.link': 'Link for dispatcher',
  'actions.replan': 'Reschedule',
  'actions.resolve': 'Resolved',
  'actions.newTime': 'New time',
  'actions.replannedTo': 'Rescheduled by the planner to',
  'actions.note': 'Note',
  'actions.notePlaceholder': 'What happened (e.g. agreed by phone)',
  'actions.resolvedNote': 'Resolved by the planner',
  'actions.confirm': 'Confirm',
  'arrivals.tabToday': "Today's loadings",
  'arrivals.readyToConnect': 'Ready to connect',
  'arrivals.showTranscript': 'Show call transcript',
  'arrivals.hideTranscript': 'Hide transcript',
  'arrivals.urgency.title': 'Carrier follow-up',
  'arrivals.urgency.rule': 'Check at 13:00 · escalate after 30 min: 2nd channel → voice call',
  'arrivals.urgency.approval': 'With approval',
  'arrivals.urgency.approvalHint': 'The system drafts the messages, the planner sends them in one click.',
  'arrivals.urgency.auto': 'Fully automatic',
  'arrivals.urgency.autoHint': 'The system follows up on its own and reports only what it could not resolve.',
  'arrivals.urgency.run': 'Run the 13:00 check',
  'arrivals.urgency.queue': 'To follow up',
  'arrivals.urgency.sendAll': 'Follow up all',
  'arrivals.urgency.result': 'Loadings without information',
  'arrivals.urgency.resultHint': 'The planner handles only what is left. No more manual calling around.',
  'arrivals.urgency.reset': 'Replay',
  'arrivals.urgency.empty': 'At 13:00 the system compares the loading plan with reality and contacts carriers we know nothing about.',
  'arrivals.detail.carrier': 'Carrier',
  'arrivals.detail.prefers': 'prefers',
  'arrivals.detail.emailDemo': 'E-mail opened (demo)',
  'arrivals.detail.driver': 'Driver',
  'arrivals.detail.name': 'Name',
  'arrivals.detail.power': 'Driving hours',
  'arrivals.detail.powerUnknown': 'unknown',
  'arrivals.detail.fromPlant': 'from the plant',
  'arrivals.detail.parking': 'Parking',
  'arrivals.detail.parkingJesenik': 'Jeseník holding parking',
  'arrivals.detail.loading': 'Loading',
  'arrivals.detail.order': 'Delivery (SAP)',
  'arrivals.detail.customer': 'Customer',
  'arrivals.detail.material': 'Material',
  'arrivals.detail.type': 'Type',
  'arrivals.detail.communication': 'Communication',
  'arrivals.detail.noMessages': 'No communication yet',
  'arrivals.detail.auto': 'automatic',
  'arrivals.ai.confirmed': 'AI: confirmed, arrival {time}',
  'arrivals.ai.rescheduled': 'AI: not today, rescheduled to tomorrow {time}',
  'arrivals.ai.noPower': 'AI: driver out of hours, loading tomorrow 6:00, parks in Jeseník',
  'arrivals.log.check': 'Check 13:00: {total} loadings without information. {gps} found via GPS, {queue} to follow up.',
  'arrivals.log.gps': 'GPS: truck is {km} km from the plant, ETA {time}. No follow-up needed.',
  'arrivals.log.gpsShort': 'GPS: {km} km from the plant, ETA {time}',
  'arrivals.log.sapNew': 'New SAP delivery {order}, assigned to {carrier}.',
  'arrivals.log.escalate': '30 min without an answer ({ids}): trying a second channel.',
  'arrivals.log.call': 'Voice call: {name}, {phone}',
  'arrivals.log.callNoAnswer': 'Voice call: {name} not answering. Handed to the planner.',
  'arrivals.log.callNoAnswerShort': 'Call not answered, handed to the planner',
  'arrivals.log.done': 'Follow-up finished. Only what needs a human is left.',

  // ── Help tips (#113) ──
  'help.label': "Help",
  'help.states.title': "Truck states",
  'help.states.text': "How many trucks are in each state. Green = fine, orange = watch out, red = problem. Click a truck in the list to see its details.",
  'help.perHour.title': "Arrivals per hour",
  'help.perHour.text': "How many trucks should arrive each hour. A tall bar means a queue at the gate. Worth spreading out.",
  'help.filters.title': "Filters",
  'help.filters.text': "Click to see only the trucks you care about. Click again to remove the filter.",
  'help.actions.title': "Actions",
  'help.actions.text': "What needs doing now. Most important on top. Handle it and it disappears.",
  'help.followUp.title': "Carrier follow-ups",
  'help.followUp.text': "At 13:00 the system checks who has not confirmed tomorrow and messages the carrier. You do not have to call anyone.",
  'help.approval.title': "Automatic or with approval",
  'help.approval.text': "Automatic: messages go out by themselves. With approval: you see them first and confirm with one click.",
  'help.risk.title': "No-show risk",
  'help.risk.text': "How likely the truck is not to come. Based on the carrier history. High risk = call them earlier.",
  'help.announce.title': "Dispatcher link",
  'help.announce.text': "Sends the carrier a link. The dispatcher fills in the plate and driver. No copying from e-mails.",
  'help.reply.title': "Simulate a reply",
  'help.reply.text': "Type a reply like a carrier would, e.g. \"we will come tomorrow\". AI reads it and suggests what to change.",
  'help.replan.title': "Re-plan proposal",
  'help.replan.text': "AI suggests a new loading time. If you agree, click Accept. Otherwise do nothing.",
  'help.voice.title': "AI voice call",
  'help.voice.text': "AI calls the dispatcher and asks about the arrival. The result is saved to the loading.",
  'help.doc.title': "Document from a photo",
  'help.doc.text': "Take a photo of a delivery note or CMR. AI reads the numbers and fills them in. Check and confirm.",
  'help.importPaste.title': "Paste a plan",
  'help.importPaste.text': "Copy the plan from Excel or an e-mail and paste it here. The system creates the loadings.",
  'help.importPhoto.title': "Plan from a photo",
  'help.importPhoto.text': "Plan only on paper? Take a photo or upload a PDF. AI types it in for you.",
  'help.period.title': "Period",
  'help.period.text': "Pick the period you want to see. All charts below recalculate.",
  'help.kpis.title': "Key numbers",
  'help.kpis.text': "Four numbers that tell you if it works. The arrow shows the change against the time before the pilot.",
  'help.ask.title': "Ask",
  'help.ask.text': "Type a question in plain words, e.g. \"Which carrier is late most often?\". AI answers from the numbers on this page.",
  'help.carriers.title': "Carriers",
  'help.carriers.text': "Who drives well and who does not. Red = often does not show up. Click a carrier to see more.",
  'help.value.title': "Pilot value",
  'help.value.text': "How much the system saves you a month. You can change the numbers in the boxes and the result recalculates.",
  'help.heatmap.title': "When trucks come",
  'help.heatmap.text': "Darker cell = more trucks on that day and hour. Shows when the site is busiest.",
  'help.reports.title': "Reports",
  'help.reports.text': "Ready-made overviews for meetings. Just open, print or send.",
  'help.rating.title': "Carrier rating",
  'help.rating.text': "A monthly report card for the carrier. Print it or send it as PDF. Help is not printed.",
  'help.energy.title': "Grinding plan",
  'help.energy.text': "AI suggests when to grind so it runs on cheap electricity. If you like the plan, take it over.",
  'help.ai.title': "AI or AI demo",
  'help.ai.text': "AI = Claude just wrote this answer. AI demo = a prepared sample answer when AI is not switched on.",
  // ── Site now (#115) ──
  'site.title': "Site now",
  'site.summary': "{total} trucks are on site.",
  'site.lateSummary': "{late} of them have been here more than {limit} minutes.",
  'site.lateHint': "Here too long. Find out why – waiting costs money.",
  'site.onSite': "on site",
  'site.empty': "Nobody",
  'site.zone.queue': "Queue at the gate",
  'site.zone.PST': "Loading PST (pallets)",
  'site.zone.CLS': "Loading CLS (silos)",
  'site.zone.weighbridge': "Weighbridge",
  'help.siteNow.title': "Site now",
  'help.siteNow.text': "Who is on site right now and where. Big number = time at that spot, small = total time on site. Red = more than 45 minutes, click and sort it out.",
  // ── Today's sections (#117) ──
  'arrivals.section.action': "Needs action",
  'arrivals.section.onSite': "On site",
  'arrivals.section.enRoute': "On the way",
  'arrivals.section.confirmed': "Confirmed",
  'arrivals.section.later': "Moved to later",
  'arrivals.section.loaded': "Loaded",
  'arrivals.typeLabel': "Type",
  // ── Recommended action (#118) ──
  'actions.more': "More…",
  'actions.recommended': "Recommended",
  'actions.reason.noInfo': "the carrier has not answered yet, send a message.",
  'actions.reason.noAnswer': "you followed up and got no answer, call.",
  'actions.reason.noPower': "the driver is out of hours, move the loading.",
  'arrivals.urgency.bar.idle': "The check has not run yet.",
  'arrivals.urgency.bar.review': "Messages are waiting to be sent.",
  'arrivals.urgency.bar.done': "The check is done.",
  'arrivals.urgency.bar.sending': "Sending messages to carriers…",
  'arrivals.urgency.barToggle': "Show the follow-up progress",
};

export default en;
